import {Button, TextArea} from '@kit';

import React, {useCallback, useRef, useState} from 'react';

import {getError} from '../utils';
import {MAX_COMMENT_LENGTH} from './constants';

import css from './comments.module.css';

type Props = {
	/** начальный текст: при ответе это «Имя, » */
	initialText?: string;
	submitLabel: string;
	autoFocus?: boolean;
	placeholder?: string;
	/** отправляет текст; ошибка из промиса показывается под полем */
	onSubmit: (text: string) => Promise<unknown>;
	/** без обработчика кнопки «Отмена» нет */
	onCancel?: () => void;
};

export const CommentForm: React.FC<Props> = ({
	initialText = '',
	submitLabel,
	autoFocus,
	placeholder = 'Напишите комментарий',
	onSubmit,
	onCancel,
}) => {
	const [text, setText] = useState(initialText);
	const [error, setError] = useState<string>();
	const [sending, setSending] = useState(false);
	const textRef = useRef<HTMLTextAreaElement>(null);

	const handleSubmit = useCallback(
		(event: React.FormEvent) => {
			event.preventDefault();
			if (sending) return;
			setSending(true);
			setError(undefined);
			onSubmit(text)
				.then(() => setText(''))
				.catch((e) => setError(getError(e)?.message))
				.finally(() => setSending(false));
		},
		[onSubmit, sending, text],
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') handleSubmit(event);
		},
		[handleSubmit],
	);

	const tooLong = text.length > MAX_COMMENT_LENGTH;

	return (
		<form className={css.form} onSubmit={handleSubmit}>
			<TextArea
				value={text}
				onChange={setText}
				setRef={textRef}
				autoFocus={autoFocus}
				autoHeightAdjustable
				placeholder={placeholder}
				onKeyDown={handleKeyDown}
				error={tooLong ? `Не больше ${MAX_COMMENT_LENGTH} символов` : undefined}
			/>
			{error && <div className={css.error}>{error}</div>}
			<div className={css.formButtons}>
				<Button type="submit" appearance="primary" loading={sending} disabled={!text.trim() || tooLong}>
					{submitLabel}
				</Button>
				{onCancel && <Button onClick={onCancel}>Отмена</Button>}
			</div>
		</form>
	);
};
